import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, themeQuartz } from "ag-grid-community";
import { useMemo } from "react";
import { Currency, RelocationStatus } from "@/gql/graphql";
import dayjs from "dayjs";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useSearch } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import {
  ReportRelocationListItem,
  reportRelocationListQuery,
} from "@/app/Reports/Reports/Components/ReportRelocations/reportRelocationsQuery";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Badge } from "@/components/catalyst/badge";
import { relocationStatusColorMap } from "@/app/Relocations/Utils/relocationStatusColorMap";

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

function dateColumn(field: keyof ReportRelocationListItem, headerName: string) {
  return {
    field,
    headerName,
    filter: "agDateColumnFilter",
    valueFormatter: (params: any) =>
      params.value ? dayjs(params.value).format("DD/MM/YYYY") : "",
  } satisfies ColDef<ReportRelocationListItem>;
}

function rateColumn(field: keyof ReportRelocationListItem, headerName: string) {
  return {
    field,
    headerName,
    valueFormatter: (params: any) => {
      const currency = params.data?.currency ?? Currency.Aud;
      return formatCurrency(params.value, currency, { emptyValue: "" });
    },
  } satisfies ColDef<ReportRelocationListItem>;
}

export function ReportRelocationsTable({ ref }: { ref: any }) {
  const { startDate, endDate } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/relocations",
  });
  const { supplierId, supplier } = useGlobalSupplier();

  const tVehicle = useTranslations("vehicle");
  const tRelocation = useTranslations("relocation");

  const { data, isLoading, isError } = useGqlQuery(reportRelocationListQuery, {
    rangeStartDate: startDate,
    rangeEndDate: endDate,
    supplierId: supplierId ?? undefined,
  });

  const columns = useMemo(() => {
    return [
      {
        field: "relocation_reference",
        headerName: "Reference",
        filter: "agTextColumnFilter",
      },
      {
        field: "status",
        headerName: "Status",
        filter: true,
        enableRowGroup: true,
        valueFormatter: (params) =>
          params.value
            ? tRelocation(`status.${params.value as RelocationStatus}`)
            : "",
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.value) {
            return null;
          }
          const status = params.value as RelocationStatus;
          return (
            <Badge
              color={relocationStatusColorMap[status]}
              className="capitalize"
            >
              {tRelocation(`status.${status}`)}
            </Badge>
          );
        },
        filterParams: {
          valueFormatter: (params: any) =>
            tRelocation(`status.${params.value as RelocationStatus}`),
        },
      },
      dateColumn("created_at", "Created"),
      {
        field: "supplier_name",
        headerName: "Supplier",
        filter: true,
        initialHide: !!supplier,
        enableRowGroup: true,
      },
      {
        field: "vehicle_name",
        headerName: "Vehicle",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        field: "vehicle_type",
        headerName: "Vehicle Type",
        filter: true,
        enableRowGroup: true,
        valueFormatter: (params) =>
          params.value ? tVehicle(`type.${params.value}`) : "",
      },
      {
        field: "vehicle_transmission",
        headerName: "Transmission",
        filter: true,
        initialHide: true,
        enableRowGroup: true,
      },
      {
        field: "departure_office_name",
        headerName: "Departure Office",
        filter: "agTextColumnFilter",
      },
      {
        field: "delivery_office_name",
        headerName: "Delivery Office",
        filter: "agTextColumnFilter",
      },
      {
        field: "region",
        headerName: "Region",
        filter: true,
        enableRowGroup: true,
      },
      dateColumn("available_from_date", "Available From"),
      dateColumn("available_to_date", "Available To"),
      dateColumn("latest_departure_date", "Latest Departure"),
      {
        field: "remaining",
        headerName: "Remaining",
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        field: "successful_bookings",
        headerName: "Successful Bookings",
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        field: "failed_bookings",
        headerName: "Failed Bookings",
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        field: "total",
        headerName: "Total",
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        field: "currency",
        headerName: "Currency",
        filter: true,
        initialHide: true,
      },
      {
        field: "hire_unit_type",
        headerName: "Hire Unit",
        filter: true,
        initialHide: true,
      },
      rateColumn("hire_unit_rate", "Hire Unit Rate"),
      rateColumn("extra_hire_unit_rate", "Extra Hire Unit Rate"),
      {
        field: "hire_units_allowed",
        headerName: "Hire Units Allowed",
        filter: "agNumberColumnFilter",
        initialHide: true,
      },
      {
        field: "extra_hire_units_allowed",
        headerName: "Extra Hire Units Allowed",
        filter: "agNumberColumnFilter",
        initialHide: true,
      },
      {
        field: "distance_allowed",
        headerName: "Distance Allowed",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value == null ? "Unlimited" : `${params.value}`,
      },
      {
        field: "fuel_description",
        headerName: "Fuel",
        filter: "agTextColumnFilter",
        initialHide: true,
      },
      {
        field: "ferry_description",
        headerName: "Ferry",
        filter: "agTextColumnFilter",
        initialHide: true,
      },
    ] satisfies ColDef<ReportRelocationListItem>[];
  }, [supplier, tVehicle, tRelocation]);

  if (isError) {
    return <div>An error occurred while retrieving report</div>;
  }

  return (
    <AgGridReact<ReportRelocationListItem>
      className="grow"
      ref={ref}
      rowData={data?.reportRelocations ?? []}
      loading={isLoading}
      theme={imoovaTheme}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
    />
  );
}
